import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DndContext,
  closestCenter,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  rectSwappingStrategy,
  arraySwap,
} from '@dnd-kit/sortable';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/AddCircle';
import Select from 'components/form/CustomSelect';
import Button from 'components/Button';
import { saveQuestion, deleteQuestion } from 'services/connect-card.service';
import QuestionSelectionModal from './QuestionSelectionModal';
import Question from './Question';
import ConfigureQuestionModal from './builders/ConfigureQuestionModal';
import { snack } from 'utils/toast';
import singleStepImg from '../../assets/single_step.svg';
import multiStepImg from '../../assets/multi_step.svg';

const StepOptions = [
  { label: 'Standard Card', value: 'standard', img: singleStepImg },
  { label: 'Multi-Step Card', value: 'multistep', img: multiStepImg },
];

const QuestionsBuilder = ({
  data,
  questions,
  setQuestions,
  selectedQuestion,
  setSelectedQuestion,
  onQuestionSelect,
  onInputChange,
  onRadioChange,
  onStepChange,
  onQuestionUpdate,
}) => {
  const [t] = useTranslation('connectCard');
  const [showAddQuestionModal, setShowAddQuestionModal] = useState(false);
  const [showConfigureQuestionModal, setShowConfigureQuestionModal] =
    useState(null);

  const { card_type } = data;

  const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor));

  const onQuestionTypeSelect = async (type) => {
    try {
      setShowConfigureQuestionModal({
        question_type: type,
        question_settings: {},
        question_possible_answers: [],
        connect_card_id: data.id,
        question_enabled: 1,
        sort_order: questions.length,
      });
      setShowAddQuestionModal(false);
    } catch (e) {
      snack.error(e.message);
    }
  };

  const onQuestionChange = (question) => {
    setQuestions(questions.map((q) => (q.id === question.id ? question : q)));
  };

  const onRemoveQuestion = async (question) => {
    setQuestions(questions.filter((q) => q.id !== question.id));
    if (question.hash) {
      await deleteQuestion(question.hash);
    }
  };

  async function handleDragEnd(event) {
    const { active, over } = event;
    if (active.id !== over.id) {
      const oldIndex = questions.findIndex((q) => q.id === active.id);
      const newIndex = questions.findIndex((q) => q.id === over.id);
      setQuestions(arraySwap(questions, oldIndex, newIndex));
      await Promise.all([
        saveQuestion({ hash: questions[oldIndex].hash, sort_order: newIndex }),
        saveQuestion({ hash: questions[newIndex].hash, sort_order: oldIndex }),
      ]).catch((e) => snack.error(e.message));
    }
  }

  return (
    <Box>
      <Select
        fullWidth
        sx={{ mb: 2 }}
        label={t('select-card-type', 'Select Your Connect Card Type')}
        value={card_type}
        options={StepOptions}
        onChange={(val) =>
          onInputChange({
            target: { name: 'card_type', value: val },
          })
        }
      />
      <Typography paragraph fontWeight={500}>
        {t('questions', 'Questions')}
      </Typography>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        modifiers={[restrictToVerticalAxis]}
        autoScroll
      >
        <SortableContext
          items={questions || []}
          strategy={rectSwappingStrategy}
        >
          <Box>
            {questions?.map((question, idx) => (
              <Question
                key={question.id}
                index={idx}
                question={question}
                selected={selectedQuestion?.id === question.id}
                onSelect={onQuestionSelect}
                onQuestionChange={onQuestionChange}
                onRemoveQuestion={onRemoveQuestion}
                onConfigure={setShowConfigureQuestionModal}
              />
            ))}
          </Box>
        </SortableContext>
      </DndContext>
      <Button
        startIcon={<AddIcon />}
        variant="text"
        onClick={() => setShowAddQuestionModal(true)}
      >
        {t('add-question', 'Add question')}
      </Button>
      {showAddQuestionModal && (
        <QuestionSelectionModal
          onSelect={onQuestionTypeSelect}
          onClose={() => setShowAddQuestionModal(false)}
        />
      )}
      {showConfigureQuestionModal && (
        <ConfigureQuestionModal
          onQuestionUpdate={onQuestionUpdate}
          question={showConfigureQuestionModal}
          onClose={() => setShowConfigureQuestionModal(null)}
        />
      )}
      <Box mt={4}>
        <Button fullWidth onClick={onStepChange}>
          {t('common:next', 'Next')}
        </Button>
      </Box>
    </Box>
  );
};

export default QuestionsBuilder;
